import {
  constructPageViewData,
  getActiveSession,
  getAvailablePrice,
  mscGet,
  mscRemove,
  mscSet,
  RootState,
  saltedHash,
  trackEvent,
} from '@msc-b2c/shared-utils';
import { LoginMode, LoginStatusAnalyticsEnum, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import {
  GetBookingMyMscResponse,
  GetShopExcursionMyMSCResponse,
  MyMscExcursion,
  OBSI,
  RetrieveVoyagerClubInfoMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import { CartItem, OBSObjI } from '@msc-b2c/interfaces/types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getPriceFromPriceBasis,
  isPerChildService,
} from '../postbookingresults/utils/PostBookingResultsUtilities';
import { triggerBookingStatusEvent } from './myAreaAnalytics';
import dayjs from 'dayjs';

const PENDING_NO_BOOKINGS_TARGET_KEY = 'pendingNoBookingsTarget';
const PENDING_BOOKING_STATUS_KEY = 'pendingBookingStatusAnalytics';

export type AnalyticsCard = MyMscExcursion | OBSObjI;
export type ShopAnalyticsResponse = GetShopExcursionMyMSCResponse | OBSI;
export type AnalyticsPriceDetails = {
  beforeDiscount?: string;
  afterDiscount?: string;
  beforePromotionAndDiscount?: string;
};
export type EnrichedAnalyticsCard = AnalyticsCard & {
  index?: number;
  promoPills?: string;
  itemCategory2?: string;
  itemListName?: string;
  selectedPassengerQuantity?: number;
};

export function getContainerInfo(element: Element | null): string {
  if (!element || !window.containerMap) {
    return 'no-ref';
  }

  let targetElement: Element | null = element;

  while (targetElement) {
    if (targetElement.hasAttribute('data-react-component')) {
      const reactComponentAttr = targetElement.getAttribute('data-react-component');
      if (reactComponentAttr) {
        const parts = reactComponentAttr.split('-');
        const hash = parts.length > 1 ? parts.pop() ?? '' : '';
        if (hash && window.containerMap[hash]) {
          return window.containerMap[hash];
        }
      }
    }
    targetElement = targetElement.parentElement;
  }

  targetElement = element;
  while (targetElement && !targetElement.hasAttribute('data-container-hash')) {
    targetElement = targetElement.parentElement;
  }

  if (targetElement) {
    const hash = targetElement.getAttribute('data-container-hash') ?? '';
    return window.containerMap[hash] ?? 'no-ref';
  }

  return 'no-ref';
}

export function calculateDiscount(
  originalPrice: string | undefined,
  discountedPrice: string | undefined,
  shouldRoundDown?: boolean,
  fallbackValue?: number
): number | undefined {
  if (!originalPrice || !discountedPrice) {
    return fallbackValue;
  }

  const diff = Number((parseFloat(originalPrice) - parseFloat(discountedPrice)).toFixed(2));
  const finalDiff = shouldRoundDown ? Math.floor(diff) : diff;

  return Math.max(finalDiff, 0);
}

export function getDiscountValue(
  card: AnalyticsCard | undefined,
  shouldRoundDown?: boolean
): number | undefined {
  if (!card) {
    return 0;
  }

  const total =
    isPerChildService(card) && card.priceBasis === PriceBasisEnum.PER_PERSON
      ? card.prices?.perChild?.total
      : card.priceBasis === PriceBasisEnum.PER_PERSON
      ? card.prices?.perAdult?.total
      : card.prices?.perCabin?.total;

  if (!total?.beforeDiscount) {
    return 0;
  }

  return calculateDiscount(total.beforeDiscount, total.afterDiscount || '0', shouldRoundDown, 0);
}

export function getShopResponseUnitPriceDetails(
  card: AnalyticsCard | undefined,
  shopResponse: ShopAnalyticsResponse | undefined
): AnalyticsPriceDetails | undefined {
  if (card?.priceBasis === PriceBasisEnum.PER_PERSON) {
    const applicablePassengerPrice = shopResponse?.prices?.perPassenger?.find(
      (passenger) => passenger.isApplicable && passenger.total
    )?.total;

    if (applicablePassengerPrice) {
      return applicablePassengerPrice;
    }
  }

  return shopResponse?.prices?.total;
}

export function getPriceDiscountValue(
  priceDetails: AnalyticsPriceDetails | undefined,
  shouldRoundDown?: boolean
): number | undefined {
  const originalPrice = priceDetails?.beforePromotionAndDiscount || priceDetails?.beforeDiscount;
  const discountedPrice = priceDetails?.afterDiscount || priceDetails?.beforeDiscount;

  return calculateDiscount(originalPrice, discountedPrice, shouldRoundDown);
}

export function getViewItemPrice(
  card: AnalyticsCard | undefined,
  shopResponse: ShopAnalyticsResponse | undefined
): number {
  const responsePrice = getAvailablePrice(getShopResponseUnitPriceDetails(card, shopResponse));
  const cardPrice = getPriceFromPriceBasis(card, '', '').price;

  return Math.ceil(parseFloat(responsePrice || cardPrice || '0'));
}

export function getCardUnitPrice(card: AnalyticsCard | undefined, fallbackValue?: string): number {
  const cardPrice = getPriceFromPriceBasis(card, '', '').price;
  return Math.ceil(parseFloat(cardPrice || fallbackValue || '0'));
}

export function setPendingBookingStatusAnalytics(): void {
  mscSet('session', PENDING_BOOKING_STATUS_KEY, true);
}

export function consumePendingBookingStatusAnalytics(): boolean {
  const isPending = mscGet<boolean>('session', PENDING_BOOKING_STATUS_KEY);

  if (!isPending) {
    return false;
  }

  mscRemove('session', PENDING_BOOKING_STATUS_KEY);
  return true;
}

function normalizeAnalyticsPath(rawUrl: string): string {
  const normalizedUrl = new URL(rawUrl, window.location.origin);
  const normalizedPath = normalizedUrl.pathname.replace(/\.html$/, '').replace(/\/+$/, '');

  return normalizedPath || '/';
}

function isPendingNoBookingsTargetPage(targetUrl: string): boolean {
  return normalizeAnalyticsPath(targetUrl) === normalizeAnalyticsPath(window.location.href);
}

export function setPendingNoBookingsAnalytics(targetUrl?: string): void {
  if (!targetUrl) {
    return;
  }

  mscSet('session', PENDING_NO_BOOKINGS_TARGET_KEY, targetUrl, 365);
}

export function triggerPendingNoBookingsAnalyticsAfterPageView(
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null
): void {
  const { goldenId } = getActiveSession();
  const targetUrl = mscGet<string>('session', PENDING_NO_BOOKINGS_TARGET_KEY);

  if (!targetUrl || !isPendingNoBookingsTargetPage(targetUrl) || !membershipData) {
    return;
  }

  mscRemove('session', PENDING_NO_BOOKINGS_TARGET_KEY);
  triggerBookingStatusEvent(
    null,
    LoginStatusAnalyticsEnum.FULL_LOGIN,
    undefined,
    null,
    'no booking',
    membershipData.memberships[0]?.points?.current,
    undefined,
    goldenId
  );
}

export const enrichPrivateAreaPageView = (
  pageViewData: Record<string, any>,
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null
) => {
  const session = getActiveSession();
  const isClubMember =
    membershipData?.memberships &&
    membershipData.memberships.length > 0 &&
    membershipData.memberships.some((m) => m.status === 'ACTIVE');
  const hasPastBookings =
    isClubMember && membershipData?.memberships.some((m) => m.cruises && m.cruises?.length > 0);
  const hasFutureBookings = session?.cruiseId;
  const currentPathname = window.location.pathname;
  const pageLanguage = window.pageViewContent?.page_language;
  const virtualPageName = pageLanguage
    ? currentPathname.replace('.html', '').split(`/${pageLanguage}/`)[1] ?? currentPathname
    : currentPathname;
  const containerList = window.containerMap
    ? Object.values(window.containerMap)
        .filter((v) => !v.includes('notAssignedType_notAssignedName'))
        .sort((a, b) => parseInt(a.split('_')[0]) - parseInt(b.split('_')[0]))
        .join(';')
    : '';

  return {
    ...pageViewData,
    user: {
      user_id: session.email ? saltedHash(session.email) : 'not available',
      // user_status: "verified",
      // user_typology: "premium",
      isClubMember: isClubMember ? 'yes' : 'no',
      pastBookingsIfClubMember: hasPastBookings ? 'yes' : 'no',
      futureCruises: hasFutureBookings ? 'yes' : 'no',
      membership_status: membershipData?.memberships[0]?.cardType?.toLowerCase() ?? 'not available',
      visitor_is_customer: 'no', // TODO: Update when private area purchase is integrated
      // visitor_is_customer: "yes"
    },
    page: {
      ...pageViewData.page,
      virtual_page_name: virtualPageName,
      website_area: 'personal area',
      container_list: containerList,
    },
  };
};

export const usePrivateAreaPageViewTracking = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { membershipData } = useSelector((state: RootState) => state.myCruise);
  const { loginMode, name } = getActiveSession();

  useEffect(() => {
    if (!enabled || (!membershipData && name && loginMode === LoginMode.FULL)) {
      return;
    }

    const pageViewData = enrichPrivateAreaPageView(constructPageViewData(), membershipData);
    trackEvent('page_view', pageViewData);

    if (Array.isArray(window.adobeDataLayer)) {
      window.adobeDataLayer.forEach((item, index) => {
        if (
          index > 0 &&
          item.event === 'page_view' &&
          window.adobeDataLayer[index].page?.virtual_page_name &&
          window.adobeDataLayer[index].page.virtual_page_name ===
            window.adobeDataLayer[index - 1].page?.virtual_page_name
        ) {
          window.adobeDataLayer.splice(index - 1, 1);
        }
      });
    }

    triggerPendingNoBookingsAnalyticsAfterPageView(membershipData);
  }, [enabled, loginMode, name, membershipData]);
};

export const getActiveCruiseCabinCount = (
  booking?: GetBookingMyMscResponse | null,
  fallbackCount = 1
): string => {
  const cabins = booking?.cabins ?? [];
  const activeCruiseId = getActiveSession().cruiseId;

  if (activeCruiseId) {
    return cabins.filter((cabin) => cabin?.cruise?.id === activeCruiseId).length.toString();
  }

  return (cabins.length || fallbackCount).toString();
};

export const getSeasonFromDepartureDate = (departureDate?: string): string => {
  if (!departureDate) {
    return '';
  }

  const departure = dayjs(departureDate);
  if (!departure.isValid()) {
    return '';
  }

  const month = departure.month();
  const year = departure.year();
  const getTwoDigitYear = (value: number) => String(value % 100).padStart(2, '0');

  if (month >= 4 && month <= 9) {
    return `sum${getTwoDigitYear(year)}`;
  }

  const winterStartYear = month >= 10 ? year : year - 1;
  const winterEndYear = winterStartYear + 1;

  return `win${getTwoDigitYear(winterStartYear)}${getTwoDigitYear(winterEndYear)}`;
};

//Function to identify if the card is OBS
export function isOBSObjI(card: AnalyticsCard | undefined | null): card is OBSObjI {
  return (card as OBSObjI).type !== undefined;
}

function getItemListNameFromCart(card: EnrichedAnalyticsCard | undefined | null): string {
  const cart = mscGet<CartItem[]>('session', 'cart') ?? [];
  if (!card?.code || !Array.isArray(cart)) {
    return '';
  }

  return (
    cart.find(
      (cartItem) =>
        cartItem?.code === card.code &&
        (!card.startDate || !cartItem.startDate || cartItem.startDate === card.startDate)
    )?.itemListName ?? ''
  );
}

export function buildItemListNameValue(itemCategory2: string, carouselName?: string): string {
  const virtualPageName = window?.location?.pathname?.replace('.html', '');
  return carouselName
    ? `listing - ${virtualPageName} - ${carouselName}`
    : `listing - ${virtualPageName} - ${itemCategory2}`;
}

export function defineItemListName(
  card: AnalyticsCard | undefined | null,
  extraData?: {
    itemCategory2?: string;
    carouselName?: string;
  }
): string {
  const effectiveItemCategory2 = extraData?.itemCategory2?.length
    ? extraData.itemCategory2
    : isOBSObjI(card)
    ? card.type.code
    : 'excursions';

  return buildItemListNameValue(effectiveItemCategory2, extraData?.carouselName);
}

export function resolveItemListName(
  card: EnrichedAnalyticsCard,
  effectiveItemCategory2: string
): string {
  return (
    card.itemListName ||
    getItemListNameFromCart(card) ||
    buildItemListNameValue(effectiveItemCategory2)
  );
}

export function getSelectedPassengerQuantityFromSelection(
  passengersToAdd?: Record<string, Record<string, boolean>> | null
): number {
  if (!passengersToAdd) {
    return 0;
  }

  const selectedPassengersCount = Object.values(passengersToAdd).reduce(
    (count, cabinPassengers) =>
      count + Object.values(cabinPassengers).filter((isSelected) => isSelected).length,
    0
  );

  return selectedPassengersCount;
}

export function getSelectedPassengerQuantityFromCart(
  card: EnrichedAnalyticsCard | undefined | null
): number {
  const cart = mscGet<CartItem[]>('session', 'cart') ?? [];
  if (!Array.isArray(cart) || !card?.code) {
    return 0;
  }

  const startDate = 'startDate' in card ? card.startDate : undefined;
  const cartItem = cart.find(
    (item) =>
      item?.code === card.code && (!startDate || !item.startDate || item.startDate === startDate)
  );

  return cartItem?.passengers?.length ?? 0;
}
